import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { 
  Box, 
  Tabs, 
  Tab, 
  Snackbar, 
  Alert, 
  CircularProgress,
  Chip
} from "@mui/material";
import axiosInstance from "../axiosinstance";
import API_URL from "../Config";
import FormComponent from "./FormComponent";

/**
 * ============================================
 * SIMPLIFIED CONFIGURATION
 * ============================================
 */
const SIMPLE_CONFIG = {
  // Form name to key mapping
  FORMS: {
    "Flange Blinding": { 
      key: "blinding", 
      label: "Flange Blinding",
      color: "#3f51b5", 
      icon: "🛡️",
      historyKey: "blinding" 
    },
    "Flange Deblinding": { 
      key: "deblinding",
      label: "Flange Deblinding", 
      color: "#e91e63", 
      icon: "🔓",
      historyKey: "deblinding" 
    },
    "Flange Tightening": { 
      key: "tightening",
      label: "Flange Tightening", 
      color: "#ff9800", 
      icon: "🔧",
      historyKey: "tightend" 
    },
    "Flange Testing": { 
      key: "testing",
      label: "Flange Testing", 
      color: "#4caf50", 
      icon: "🧪",
      historyKey: "tested" 
    },
    "Flange Retesting": { 
      key: "testing",
      label: "Flange Retesting", 
      color: "#4caf50", 
      icon: "🧪",
      historyKey: "tested" 
    },
    "Flange Retightening": { 
      key: "tightening",
      label: "Flange Retightening", 
      color: "#ff9800", 
      icon: "🔧",
      historyKey: "tightend" 
    }
  },

  // Tab order for display
  TAB_ORDER: ["blinding", "deblinding", "tightening", "testing"],

  // Display config
  DISPLAY: {
    blinding: {
      label: "Flange Blinding",
      shortLabel: "Blinding",
      color: "#3f51b5",
      // icon: "🛡️",
      historyKey: "blinding"
    },
    deblinding: {
      label: "Flange Deblinding",
      shortLabel: "Deblinding",
      color: "#e91e63",
      // icon: "🔓",
      historyKey: "deblinding"
    },
    tightening: {
      label: "Flange Tightening",
      shortLabel: "Tightening",
      color: "#ff9800",
      // icon: "🔧",
      historyKey: "tightend"
    },
    testing: {
      label: "Flange Testing",
      shortLabel: "Testing",
      color: "#4caf50",
      // icon: "🧪",
      historyKey: "tested"
    }
  }
};

/**
 * ============================================
 * SIMPLIFIED FLANGE FORM COMPONENT
 * For basic usage without advanced features
 * ============================================
 */
function FlangeFormNew() {
  const { flangeId } = useParams();

  // ========== STATE ==========
  const [currentForm, setCurrentForm] = useState(null);
  const [currentFormName, setCurrentFormName] = useState(null);
  const [availableForms, setAvailableForms] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [flangeData, setFlangeData] = useState({});
  const [formData, setFormData] = useState({});
  const [imageData, setImageData] = useState({});
  const [historyData, setHistoryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionButton, setActionButton] = useState(null);
  const [isRetryMode, setIsRetryMode] = useState(false);
  const [currentIteration, setCurrentIteration] = useState(null);
  const [snackbar, setSnackbar] = useState({ 
    open: false, 
    message: "", 
    severity: "success" 
  });

  const tabs = SIMPLE_CONFIG.TAB_ORDER;

  /**
   * ============================================
   * FETCH FLANGE DATA ON MOUNT
   * ============================================
   */
  useEffect(() => {
    if (flangeId) {
      fetchFlangeData();
      fetchHistoryData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flangeId]);

  /**
   * ============================================
   * FETCH FLANGE DATA FROM BACKEND
   * ============================================
   */
  const fetchFlangeData = async () => {
    setLoading(true);

    try {
      const authKey = localStorage.getItem("authKey");
      const response = await axiosInstance.get(
        `${API_URL}/api/get_flange_detail_by_id/${flangeId}`,
        { headers: { Authorization: `Bearer ${authKey}` } }
      );

      const data = response.data;
      const formInfo = data.form_info;

      console.log("📡 Flange Data:", data);

      // Get current form config
      const currentConfig = SIMPLE_CONFIG.FORMS[formInfo.current_form];
      
      // Get previous form configs
      const previousConfigs = (formInfo.previous_forms || [])
        .map(name => SIMPLE_CONFIG.FORMS[name])
        .filter(Boolean);

      // Build available forms (unique keys)
      const available = [
        ...new Set([
          ...previousConfigs.map(c => c.key), 
          currentConfig?.key
        ])
      ].filter(Boolean);

      // Check if retry mode
      const isRetry = formInfo.current_form?.includes("Retesting") ||
                      formInfo.current_form?.includes("Retightening");

      setCurrentForm(currentConfig?.key);
      setCurrentFormName(formInfo.current_form);
      setAvailableForms(available);
      setFlangeData(data);
      setActionButton(
        formInfo.action_button !== "null" && formInfo.action_button !== null
          ? formInfo.action_button
          : null
      );
      setIsRetryMode(isRetry);
      setCurrentIteration(data.current_iteration || null);

      // Set active tab to current form
      const tabIndex = tabs.indexOf(currentConfig?.key);
      if (tabIndex >= 0) {
        setActiveTab(tabIndex);
      }

      setLoading(false);
    } catch (error) {
      console.error("❌ Error fetching flange data:", error);
      setSnackbar({ 
        open: true, 
        message: "Error loading data", 
        severity: "error" 
      });
      setLoading(false);
    }
  };

  /**
   * ============================================
   * FETCH HISTORY DATA
   * ============================================
   */
  const fetchHistoryData = async () => {
    try {
      const authKey = localStorage.getItem("authKey");
      const response = await axiosInstance.get(
        `${API_URL}/api/get_flange_history/${flangeId}`,
        { headers: { Authorization: `Bearer ${authKey}` } }
      );

      console.log("📚 History Data:", response.data);

      // Get current tab's history
      const currentTabKey = tabs[activeTab];
      const historyKey = SIMPLE_CONFIG.DISPLAY[currentTabKey]?.historyKey || currentTabKey;

      if (response.data.data) {
        const historySection = response.data.data.find(section => section[historyKey]);
        if (historySection) {
          setHistoryData(historySection[historyKey] || []);
        }
      }
    } catch (error) {
      console.error("❌ Error fetching history:", error);
    }
  };

  /**
   * ============================================
   * HANDLE TAB CHANGE
   * ============================================
   */
  const handleTabChange = (event, newValue) => {
    const formKey = tabs[newValue];
    
    if (!availableForms.includes(formKey)) {
      setSnackbar({
        open: true,
        message: "Please complete the current form before moving forward.",
        severity: "warning"
      });
      return;
    }
    
    setActiveTab(newValue);

    // Fetch history for new tab
    fetchHistoryForTab(formKey);
  };

  /**
   * ============================================
   * FETCH HISTORY FOR SPECIFIC TAB
   * ============================================
   */
  const fetchHistoryForTab = async (formKey) => {
    try {
      const authKey = localStorage.getItem("authKey");
      const response = await axiosInstance.get(
        `${API_URL}/api/get_flange_history/${flangeId}`,
        { headers: { Authorization: `Bearer ${authKey}` } }
      );

      const historyKey = SIMPLE_CONFIG.DISPLAY[formKey]?.historyKey || formKey;

      if (response.data.data) {
        const historySection = response.data.data.find(section => section[historyKey]);
        if (historySection) {
          setHistoryData(historySection[historyKey] || []);
        } else {
          setHistoryData([]);
        }
      }
    } catch (error) {
      console.error("❌ Error fetching history for tab:", error);
      setHistoryData([]);
    }
  };

  /**
   * ============================================
   * HANDLE FORM SUBMISSION
   * ============================================
   */
  const handleSubmit = async () => {
    try {
      const authKey = localStorage.getItem("authKey");
      const currentFormKey = tabs[activeTab];

      // Validation for testing forms
      if (currentFormKey === "testing") {
        if (!formData.test_result) {
          setSnackbar({
            open: true,
            message: "Test result is required!",
            severity: "error"
          });
          return;
        }
        if (!imageData.gaugePhoto) {
          setSnackbar({
            open: true,
            message: "Gauge photo is required!",
            severity: "error"
          });
          return;
        }
      }

      const formDataToSend = new FormData();

      // Add basic fields
      formDataToSend.append("date", new Date().toISOString().split("T")[0]);
      formDataToSend.append("current_process", currentFormName);
      formDataToSend.append("status", "Filled");

      // Add action button if exists
      if (actionButton) {
        formDataToSend.append("action_button", actionButton);
      }

      // Add form data fields
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== undefined && formData[key] !== "") {
          formDataToSend.append(key, formData[key]);
        }
      });

      // Add uploaded files
      if (imageData.image && Array.isArray(imageData.image)) {
        imageData.image.forEach(file => {
          formDataToSend.append("uploads", file);
        });
      }

      // Add gauge photo
      if (imageData.gaugePhoto) {
        formDataToSend.append("gauge_photo", imageData.gaugePhoto);
      }

      // Add files to delete
      if (imageData.filesToDelete && Array.isArray(imageData.filesToDelete)) {
        imageData.filesToDelete.forEach(fileId => {
          formDataToSend.append("files_to_delete", fileId);
        });
      }

      console.log("📤 Submitting form:", currentFormKey);

      const response = await axiosInstance.post(
        `${API_URL}/api/record_form_data/${flangeId}`,
        formDataToSend,
        {
          headers: {
            Authorization: `Bearer ${authKey}`,
            "Content-Type": "multipart/form-data"
          }
        }
      );

      console.log("✅ Submission successful:", response.data);

      setSnackbar({ 
        open: true, 
        message: "Form submitted successfully!", 
        severity: "success" 
      });

      // Clear form data
      setFormData({});
      setImageData({});

      // Refresh flange data and history
      await fetchFlangeData();
      await fetchHistoryForTab(currentFormKey);

      // Switch to new form if backend changed it
      if (response.data?.form_info?.current_form) {
        const newFormConfig = SIMPLE_CONFIG.FORMS[response.data.form_info.current_form];
        if (newFormConfig) {
          const newTabIndex = tabs.indexOf(newFormConfig.key);
          if (newTabIndex >= 0 && newTabIndex !== activeTab) {
            setActiveTab(newTabIndex);
          }
        }
      }
    } catch (error) {
      console.error("❌ Submit error:", error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Submission failed",
        severity: "error"
      });
    }
  };

  /**
   * ============================================
   * HANDLE ACTION BUTTON
   * ============================================
   */
  const handleActionButton = async (action) => {
    console.log("🎬 Action:", action);

    try {
      if (action.includes("Retesting") || action.includes("Start Re-Testing")) {
        const testingIndex = tabs.indexOf("testing");
        if (testingIndex >= 0) {
          setActiveTab(testingIndex);
        }
        setFormData({});
        setImageData({});
        setSnackbar({
          open: true,
          message: "Switched to Retesting mode",
          severity: "info"
        });
      } else if (action.includes("Retightening") || action.includes("Start Re-Tightening")) {
        const tighteningIndex = tabs.indexOf("tightening");
        if (tighteningIndex >= 0) {
          setActiveTab(tighteningIndex);
        }
        setFormData({});
        setImageData({});
        setSnackbar({
          open: true,
          message: "Switched to Retightening mode",
          severity: "info"
        });
      } else if (action.includes("Blinding")) {
        const blindingIndex = tabs.indexOf("blinding");
        if (blindingIndex >= 0) {
          setActiveTab(blindingIndex);
        }
        setFormData({});
        setImageData({});
        setSnackbar({
          open: true,
          message: "Switched to Blinding",
          severity: "info"
        });
      } else if (action === "Approval from Client") {
        setSnackbar({
          open: true,
          message: "Awaiting client approval",
          severity: "success"
        });
      }

      await fetchFlangeData();
    } catch (error) {
      console.error("❌ Action error:", error);
      setSnackbar({
        open: true,
        message: "Error processing action",
        severity: "error"
      });
    }
  };

  /**
   * ============================================
   * RENDER LOADING STATE
   * ============================================
   */
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  /**
   * ============================================
   * GET CURRENT TAB CONFIG
   * ============================================
   */
  const currentTabKey = tabs[activeTab];
  const currentTabConfig = SIMPLE_CONFIG.DISPLAY[currentTabKey] || {
    label: currentTabKey.charAt(0).toUpperCase() + currentTabKey.slice(1),
    shortLabel: currentTabKey.charAt(0).toUpperCase() + currentTabKey.slice(1),
    color: "#2196f3",
    icon: "📝",
    historyKey: currentTabKey
  };

  return (
    <Box>
      {/* ========== TABS ========== */}
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange} 
          centered
          sx={{
            "& .MuiTab-root": {
              minHeight: 64,
              fontSize: "0.95rem",
              fontWeight: 500
            }
          }}
        >
          {tabs.map((tab, index) => {
            const config = SIMPLE_CONFIG.DISPLAY[tab];
            const isDisabled = !availableForms.includes(tab);
            const isActive = activeTab === index;

            return (
              <Tab
                key={tab}
                label={
                  <Box display="flex" alignItems="center" gap={1}>
                    {/* <span>{config?.icon || "📝"}</span> */}
                    <span>{config?.shortLabel || tab.charAt(0).toUpperCase() + tab.slice(1)}</span>
                  </Box>
                }
                disabled={isDisabled}
                sx={{
                  color: isActive ? config?.color : "text.secondary",
                  opacity: isDisabled ? 0.4 : 1,
                  borderBottom: isActive ? `3px solid ${config?.color}` : "none",
                  "&.Mui-selected": {
                    color: config?.color
                  }
                }}
              />
            );
          })}
        </Tabs>
      </Box>

      {/* ========== CURRENT ITERATION BADGE ========== */}
      {currentIteration && (
        <Box p={2} display="flex" justifyContent="center">
          <Chip
            label={`${isRetryMode ? "Retry" : "Current"} Iteration: #${currentIteration}`}
            sx={{
              background: isRetryMode
                ? "linear-gradient(45deg, #ff9800, #ffb74d)"
                : "linear-gradient(45deg, #2196F3, #21CBF3)",
              color: "white",
              fontWeight: "bold",
              fontSize: "0.9rem",
              px: 3,
              py: 1
            }}
          />
        </Box>
      )}

      {/* ========== FORM CONTENT ========== */}
      <Box p={3}>
        <FormComponent
          formKey={currentTabKey}
          formConfig={currentTabConfig}
          isCurrentForm={currentTabKey === currentForm}
          currentFormName={currentFormName}
          actionButton={actionButton}
          formData={formData}
          onFormDataChange={(data) => setFormData({ ...formData, ...data })}
          imageData={imageData}
          onImageDataChange={(data) => setImageData({ ...imageData, ...data })}
          isEditMode={false}
          onEditModeChange={() => {}}
          historyData={historyData}
          onSubmit={handleSubmit}
          onEdit={() => {}}
          onActionButton={handleActionButton}
          flangeData={flangeData}
          isRetryMode={isRetryMode}
          showSnackbar={(msg, severity) => setSnackbar({ open: true, message: msg, severity })}
          API_URL={API_URL}
        />
      </Box>

      {/* ========== SNACKBAR ========== */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default FlangeFormNew;